/**
 * @fileoverview Shared ui-admin contract for host-provided violation code set services.
 * @module violations
 *
 * Responsibilities:
 * - Define the shared service interface used by ui-admin consumers.
 * - Provide a safe noop default for hosts that do not supply a concrete implementation.
 *
 * Dependencies:
 * - ../types/violationCodeSet
 *
 * Architecture:
 * - Shared package abstraction only; host apps remain responsible for local-first data access.
 * - The noop implementation prevents shared package consumers from inventing mock production data.
 *
 * Notes:
 * - Tenant-web and system-web should inject real services where violation selection is enabled.
 */

import type { ViolationCodeSet, ViolationIndexEntry } from '../types/violationCodeSet';

/**
 * @contract ViolationCodeSetService
 *
 * Purpose:
 * - Defines the host-provided service surface for loading code sets and searching violation entries.
 *
 * Fields:
 * - listCodeSets: Returns available code sets.
 * - searchViolations: Returns matching violation entries for a code set.
 *
 * Rules:
 * - Implementations should source data from the host app's local-first layer.
 *
 * Sync:
 * - Shared package consumers never sync directly; hosts own sync behavior.
 */
export interface ViolationCodeSetService {
  listCodeSets(): Promise<ViolationCodeSet[]>;
  searchViolations(query: string, codeSetId: string): Promise<ViolationIndexEntry[]>;
}

/**
 * @localFirst
 *
 * Source of Truth:
 * - local
 *
 * Write Flow:
 * - No writes are performed by this shared noop service.
 *
 * Sync Strategy:
 * - Concrete hosts are responsible for sync.
 *
 * Conflict Resolution:
 * - Not applicable in the noop implementation.
 */
export const noopViolationCodeSetService: ViolationCodeSetService = {
  listCodeSets: async () => [],
  searchViolations: async () => [],
};

export type { ViolationCodeSet, ViolationIndexEntry };
